import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { ProductFilters } from "../services/productService";

export const useProductFilters = (defaultFilters: ProductFilters = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<ProductFilters>(() => {
    // Initialize from URL params
    const params: ProductFilters = { ...defaultFilters };
    const categoryIds = searchParams.getAll("categoryId");
    const search = searchParams.get("search");
    const minPrice = searchParams.get("minPrice");
    const maxPrice = searchParams.get("maxPrice");
    const page = searchParams.get("page");
    const limit = searchParams.get("limit");

    if (categoryIds.length > 0) {
      params.categoryId = categoryIds.length === 1 ? categoryIds[0] : categoryIds;
    }
    if (search) params.search = search;
    if (minPrice) params.minPrice = parseFloat(minPrice);
    if (maxPrice) params.maxPrice = parseFloat(maxPrice);
    if (page) params.page = parseInt(page, 10);
    if (limit) {
      params.limit = parseInt(limit, 10);
    } else {
      // Always ensure limit is set from defaultFilters
      params.limit = defaultFilters.limit || 4;
    }
    // Always ensure page is set
    if (!params.page) {
      params.page = defaultFilters.page || 1;
    }

    return params;
  });

  // Update URL when filters change
  useEffect(() => {
    const newParams = new URLSearchParams();
    
    if (filters.categoryId) {
      if (Array.isArray(filters.categoryId)) {
        filters.categoryId.forEach((id) => newParams.append("categoryId", id));
      } else {
        newParams.set("categoryId", filters.categoryId);
      }
    }
    if (filters.search) newParams.set("search", filters.search);
    if (filters.minPrice !== undefined && filters.minPrice !== null) {
      newParams.set("minPrice", filters.minPrice.toString());
    }
    if (filters.maxPrice !== undefined && filters.maxPrice !== null) {
      newParams.set("maxPrice", filters.maxPrice.toString());
    }
    // Only include page if it's greater than 1
    if (filters.page && filters.page > 1) {
      newParams.set("page", filters.page.toString());
    }
    if (filters.limit && filters.limit !== 20) {
      newParams.set("limit", filters.limit.toString());
    }

    // If no filters are set, navigate to base catalog URL (no query params)
    // Use replace: false when clearing all filters to allow proper navigation
    const hasAnyFilters = filters.categoryId || filters.search || 
      filters.minPrice !== undefined || filters.maxPrice !== undefined ||
      (filters.page && filters.page > 1);
    
    if (!hasAnyFilters) {
      // Clear all params and navigate to base catalog
      setSearchParams({}, { replace: false });
    } else {
      setSearchParams(newParams, { replace: true });
    }
  }, [filters, setSearchParams]);

  const updateFilters = useCallback((newFilters: ProductFilters) => {
    // Merge with current filters to preserve existing values
    const updatedFilters: ProductFilters = {
      ...filters, // Preserve current filters
      ...newFilters, // Apply new filter values
    };
    
    // Ensure limit is always set (from newFilters, current filters, or default)
    if (!updatedFilters.limit) {
      updatedFilters.limit = newFilters.limit || filters.limit || defaultFilters.limit || 4;
    }
    
    // Ensure page is always set
    if (!updatedFilters.page) {
      updatedFilters.page = newFilters.page || filters.page || 1;
    }
    
    // Only reset page to 1 if explicitly clearing all filters (not when just changing page)
    // This check should only apply when clearing category/search/price filters, not when just changing page
    const isClearingAllFilters = 
      (newFilters.categoryId === undefined || newFilters.categoryId === null) &&
      (newFilters.search === undefined || newFilters.search === null || newFilters.search === '') &&
      (newFilters.minPrice === undefined || newFilters.minPrice === null) &&
      (newFilters.maxPrice === undefined || newFilters.maxPrice === null) &&
      !newFilters.page; // Only reset page if page wasn't explicitly set in newFilters
    
    if (isClearingAllFilters && !newFilters.page) {
      updatedFilters.page = 1;
    }
    
    console.log("Updating filters - newFilters:", newFilters, "current filters:", filters, "updated:", updatedFilters);
    setFilters(updatedFilters);
  }, [filters, defaultFilters]);

  return [filters, updateFilters] as const;
};

